import * as React from "react";
import { ScheduleGrid } from "./ScheduleGrid";
import { FormControlLabel, Switch, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SiteInfo } from "./SiteAboutPageUtils/siteAboutInterfaces";
import { SchedulesTabs } from "./SchedulesTabs";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { SchedulesTabAutopatrol } from "./SchedulesTabAutopatrol";
import { useFlexIgnoreZonesOnboardingStore } from "./FlexIgnoreZonesOnboarding/flexIgnoreZonesOnboardingStore";
import {
  FLEX_IZ_LD_EMPTY_CAMERA_CONTEXTS,
  FLEX_IZ_LD_EMPTY_ENABLED_BY_SITE,
  FLEX_IZ_LD_EMPTY_SYNC_LOADING,
} from "./FlexIgnoreZonesOnboarding/flexIgnoreZonesLdEmpty";
import { FlexIgnoreZonesCameraPresetCard } from "./FlexIgnoreZonesOnboarding/FlexIgnoreZonesCameraPresetCard";
import { useFlexIzScheduleSummary } from "./FlexIgnoreZonesOnboarding/useFlexIzScheduleSummary";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import {
  ScheduleOverridesNotice,
  useScheduleOverrides,
} from "./ScheduleOverridesNotice";
import { overrideWeekdaysWithinWeek } from "./SiteAboutPageUtils/scheduleOverrides";

export const SchedulesTab = ({ siteInfo }: { siteInfo: SiteInfo }) => {
  const { siteSchedules, schedulesIndex } = useSiteAboutStore();
  const { isFeatureEnabled } = useFeatureFlag();
  const showFlexIgnoreZones = isFeatureEnabled("showFlexIgnoreZones", false);
  const enabledBySite = useFlexIgnoreZonesOnboardingStore((state) =>
    showFlexIgnoreZones ? state.enabledBySite : FLEX_IZ_LD_EMPTY_ENABLED_BY_SITE
  );
  const cameraContexts = useFlexIgnoreZonesOnboardingStore((state) =>
    showFlexIgnoreZones ? state.cameraContexts : FLEX_IZ_LD_EMPTY_CAMERA_CONTEXTS
  );
  const loadCameraContext = useFlexIgnoreZonesOnboardingStore((state) =>
    showFlexIgnoreZones
      ? state.loadCameraContext
      : async (_cameraId: number, _forceRefresh?: boolean) => {}
  );
  const flexIgnoreZonesEnabledSyncLoadingBySite = useFlexIgnoreZonesOnboardingStore((state) =>
    showFlexIgnoreZones
      ? state.flexIgnoreZonesEnabledSyncLoadingBySite
      : FLEX_IZ_LD_EMPTY_SYNC_LOADING
  );

  const userInfo = JSON.parse(localStorage.userInfo);

  const permissions = userInfo.permission;

  const isSuperUser =
    userInfo.username.includes("@aegissystems") ||
    userInfo.username.includes("@actuate") ||
    userInfo.email.includes("@aegissystems") ||
    userInfo.email.includes("@actuate");
  const flexIgnoreZonesSyncLoading =
    flexIgnoreZonesEnabledSyncLoadingBySite[siteInfo.id] === true;
  const flexIgnoreZonesEnabled = enabledBySite[siteInfo.id] === true;

  const flexIzScheduleSummary = useFlexIzScheduleSummary(siteInfo, {
    showFlexIgnoreZones,
    flexScheduleActive: !!siteInfo.has_flex_schedule,
    flexIgnoreZonesEnabled,
  });

  // ENG-2631: holiday-calendar / one-off overrides replace the weekly grid
  // on their dates; list them and mark this week's affected days.
  const { overrides, todayIso } = useScheduleOverrides(
    siteInfo.id,
    siteInfo.timezone
  );
  const overrideDays = React.useMemo(
    () => overrideWeekdaysWithinWeek(overrides, todayIso),
    [overrides, todayIso]
  );

  const products = JSON.parse(localStorage.products);
  const productIds = new Map();

  products.forEach((product: any) => {
    productIds.set(product.id, product.name);
  });

  if (siteInfo.integration_type_name === "autopatrol") {
    return <SchedulesTabAutopatrol editMode={false} />;
  }

  const fullSchedulesTab = (
    <div className="pb-[10px]">
      <div className="border-t border-gray-200"></div>
      <ScheduleOverridesNotice overrides={overrides} todayIso={todayIso} />
      <div
        className={`relative top-[0px] ${
          !siteInfo.has_flex_schedule
            ? "h-[440px]"
            : siteSchedules.length >= 2
            ? "h-[510px]"
            : "h-[480px]"
        } bg-white overflow-scroll`}
      >
        <div className="flex">
          <p className="font-black font-mulish text-[20px] p-3 indent-4">
            Schedule Overview &nbsp;
            <Tooltip
              title={`Scheduled arming time ranges are highlighted in green.`}
              placement="top"
              sx={{ fontSize: "15px", fontFamily: "mulish" }}
            >
              <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
            </Tooltip>
          </p>
          {permissions.flex_schedule && (
            <div className="pl-[30px] pt-[10px] flex">
              <FormControlLabel
                sx={{ fontFamily: "Mulish" }}
                control={
                  <Switch
                    color="warning"
                    checked={siteInfo.has_flex_schedule}
                    disabled={true}
                  />
                }
                label="Flex Scheduling"
              />
              {showFlexIgnoreZones && siteInfo.has_flex_schedule && (
                <FormControlLabel
                  sx={{ fontFamily: "Mulish" }}
                  control={
                    <Switch
                      color="warning"
                      checked={!!flexIgnoreZonesEnabled}
                      disabled={true}
                      inputProps={{
                        "aria-busy": flexIgnoreZonesSyncLoading,
                      }}
                    />
                  }
                  label="Flex Ignore Zones"
                />
              )}
            </div>
          )}
        </div>
        <div className="pb-[10px]">
          {siteSchedules.length > 1 && permissions.flex_schedule && (
            <SchedulesTabs />
          )}
        </div>
        {siteInfo.has_flex_schedule && siteSchedules[schedulesIndex] && (
          <div className="flex">
            <div className="pl-[30px] pb-[10px] overflow-scroll flex">
              <div className="font-black font-mulish text-[20px] flex items-center">
                {siteSchedules[schedulesIndex].display_name}
                {isSuperUser && (
                  <div className="pl-[10px] pb-[px]">
                    <Tooltip
                      title={`${siteSchedules[schedulesIndex].log_name}`}
                      placement="top"
                      sx={{ fontSize: "15px", fontFamily: "mulish" }}
                    >
                      <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                    </Tooltip>
                  </div>
                )}
              </div>
              <p className="font-black font-mulish text-[16px] pl-[30px] pt-[5px]">
                {`Active Products: `}
              </p>
              <div className="pl-[6px] text-[16px] pt-[5px]">
                {`${siteSchedules[schedulesIndex].product
                  .map((product: number) => {
                    return `${productIds.get(product)}`;
                  })
                  .join(", ")}`}
              </div>
            </div>
          </div>
        )}
        <div className="relative top-[0px] left-[15px] w-[1500px]">
          <ScheduleGrid overrideDays={overrideDays} />
        </div>
      </div>
      {flexIzScheduleSummary && (
        <div className="pl-[15px] pr-[20px] pt-[16px] pb-[24px] bg-white">
          <p className="font-mulish text-[18px] font-black pb-[12px]">
            Selected Cameras / Flex Schedule Presets
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-[12px] justify-items-start max-w-[1500px]">
            {flexIzScheduleSummary.rows.map(({ camera, preset }) => (
              <FlexIgnoreZonesCameraPresetCard
                key={`${flexIzScheduleSummary.resolvedScheduleId}-${camera.id}`}
                scheduleId={flexIzScheduleSummary.resolvedScheduleId}
                camera={camera}
                preset={preset}
                cameraContext={cameraContexts[camera.id]}
                onPreviewImageError={() => {
                  loadCameraContext(camera.id, true);
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const remoteLinkSchedulesTab = (
    <div className="pb-[10px]">
      <div className="border-t border-gray-200"></div>
      <div className="relative top-[0px] h-[60px] bg-white overflow-scroll">
        <div className="flex">
          <p className="font-mulish text-[18px] p-3 text-center w-full">
            Remote Link enabled. See IMMIX for site schedule.
          </p>
        </div>
      </div>
    </div>
  );

  return siteInfo.has_remote_link ? remoteLinkSchedulesTab : fullSchedulesTab;
};
